import React from 'react';
import './RightPanelHeader.scss';
import { IconCancel, IconDownload, IconCopyNew, IconTrash } from 'components/Icons';
import { PromptVersion } from '../../services/PromptVersionService';

export interface RightPanelHeaderProps {
  title?: string;
  onClose?: () => void;
  onDownload?: () => void;
  onCopy?: () => void;
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  // Version management props
  versions?: PromptVersion[];
  activeVersionId?: string;
  onVersionChange?: (versionId: string) => void;
  onNewVersion?: () => void;
  onSave?: () => void;
  onDeleteVersion?: () => void;
  hasUnsavedChanges?: boolean;
  // Cache management
  onClearCache?: () => void;
}

const RightPanelHeader: React.FC<RightPanelHeaderProps> = ({
  title = 'Prompt',
  onClose,
  onDownload,
  onCopy,
  selectedModel = 'gpt-4o-mini',
  onModelChange,
  versions = [],
  activeVersionId,
  onVersionChange,
  onNewVersion,
  onSave,
  onDeleteVersion,
  hasUnsavedChanges = false,
  onClearCache,
}) => {
  const modelOptions = [
    'gpt-5',
    'gpt-5-mini',
    'gpt-5-nano',
    'gpt-4.1',
    'gpt-4.1-mini',
    'gpt-4.1-nano',
    'gpt-4o',
    'gpt-4o-mini',
  ];
  const activeVersion = versions.find(v => v.id === activeVersionId);
  const displayTitle = activeVersion ? activeVersion.name : title;
  const canDeleteActiveVersion = activeVersion && !activeVersion.isOriginal;

  // Format timestamp for display
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    }
  };

  return (
    <div className="right-panel-header">
      <div className="right-panel-header__left">
        {versions.length > 0 ? (
          <select
            className="right-panel-header__version-select"
            value={activeVersionId || ''}
            onChange={e => onVersionChange && onVersionChange(e.target.value)}
            title="Select Prompt Version"
          >
            {versions.map(version => (
              <option key={version.id} value={version.id}>
                {version.name}
                {version.isOriginal ? ' (Original)' : ''}
                {' - '}
                {formatTimestamp(version.timestamp)}
              </option>
            ))}
          </select>
        ) : (
          <div className="right-panel-header__title">{displayTitle}</div>
        )}
      </div>

      <div className="right-panel-header__actions">
        <div className="right-panel-header__version-controls">
          {onSave && (
            <button
              className={`right-panel-header__text-button--save ${
                hasUnsavedChanges
                  ? 'right-panel-header__text-button--save--enabled'
                  : 'right-panel-header__text-button--save--disabled'
              }`}
              onClick={onSave}
              disabled={!hasUnsavedChanges}
              aria-label="Save Changes"
              title={hasUnsavedChanges ? 'Save changes' : 'No changes to save'}
            >
              Save
            </button>
          )}
          {onDeleteVersion && canDeleteActiveVersion && (
            <button
              className="right-panel-header__text-button--delete"
              onClick={onDeleteVersion}
              aria-label="Delete Version"
              title="Delete current version"
            >
              Delete
            </button>
          )}
          {onNewVersion && (
            <button
              className="right-panel-header__text-button--new"
              onClick={onNewVersion}
              aria-label="Create New Version"
              title="Create new version"
            >
              New
            </button>
          )}
          {onClearCache && (
            <button
              className="right-panel-header__text-button--clear-cache"
              onClick={onClearCache}
              aria-label="Empty Cache"
              title="Empty AI analysis cache"
            >
              Empty
            </button>
          )}
        </div>
        {onModelChange && (
          <select
            className="right-panel-header__model-select"
            value={selectedModel}
            onChange={e => onModelChange(e.target.value)}
            title="Select AI Model"
          >
            {modelOptions.map(model => (
              <option key={model} value={model}>
                {model}
              </option>
            ))}
          </select>
        )}
        <div className="right-panel-header__close-button" onClick={onCopy}>
          <IconCopyNew />
        </div>
        <button className="right-panel-header__close-button" onClick={onDownload} aria-label="Download">
          <IconDownload />
        </button>
        {onClose && (
          <button className="right-panel-header__close-button" onClick={onClose} aria-label="Close">
            <IconCancel />
          </button>
        )}
      </div>
    </div>
  );
};

export default RightPanelHeader;
